import React, { useEffect, useMemo } from "react";
import Dropdown from "../../common/dropDown";
import { resolveRecipientRoleIds } from "../../../services/lookupService";
import {
  ANCHOR_TYPES,
  MILESTONE_CATEGORIES,
  NOTIFICATION_CHANNEL,
  formatMilestoneDatePreview,
  parseTags,
} from "./isfMilestoneService";
import {
  MILESTONE_NAME_MAX_LENGTH,
  normalizeOffsetDigits,
  parseMilestoneOffset,
  composeMilestoneOffset,
  milestoneMatchesFolderCategory,
} from "./milestoneLibraryShared";
import MilestoneRecipientRoles from "./MilestoneRecipientRoles";
import MilestoneRecordsGrid from "./MilestoneRecordsGrid";
import MilestoneFolderSelect from "./MilestoneFolderSelect";
import MilestoneWorkflowRoles from "./MilestoneWorkflowRoles";
import MilestoneSingleOptionPicker from "./MilestoneSingleOptionPicker";
import { normalizeFolderCategory } from "./isfFolderLibraryService";
import { STUDY_GATE_OPTIONS } from "./isfStudyGateService";

const MilestoneLibraryForm = ({
  form,
  setForm,
  errors = {},
  readOnly = false,
  lockedCategory = null,
  notificationRoles = [],
  folderOptions = [],
  milestoneLookup = [],
  tagInput,
  setTagInput,
  onAddTag,
  onRemoveTag,
  onUpdateNotification,
  onSetRecipients,
  onSetUploaderRoles,
  onSetSignerRoles,
  leftPanelRef,
  leftPanelHeight,
  hideDefaultFolder = false,
  hideWorkflowRoles = false,
  hideRecordsGrid = false,
  metaFooter = null,
}) => {
  const categoryOptions = useMemo(
    () => MILESTONE_CATEGORIES.map((c) => ({ value: c, label: c })),
    [],
  );

  const activeFolderCategory = normalizeFolderCategory(form.DefaultFolderCategory);

  const anchorMilestoneOptions = useMemo(
    () =>
      milestoneLookup
        .filter((m) => m.MilestoneCode !== form.MilestoneCode)
        .filter((m) =>
          milestoneMatchesFolderCategory(m, activeFolderCategory, folderOptions),
        )
        .map((m) => ({
          value: m.MilestoneCode,
          label: `${m.MilestoneCode} — ${m.MilestoneName}`,
        })),
    [milestoneLookup, form.MilestoneCode, folderOptions, activeFolderCategory],
  );

  useEffect(() => {
    if (form.AnchorType !== "AnotherMilestone" || !form.AnchorMilestoneCode) return;
    const stillValid = anchorMilestoneOptions.some(
      (opt) => opt.value === form.AnchorMilestoneCode,
    );
    if (!stillValid) {
      setForm((p) => ({ ...p, AnchorMilestoneCode: "" }));
    }
  }, [anchorMilestoneOptions, form.AnchorMilestoneCode, form.AnchorType, setForm]);

  const selectedCategory = useMemo(
    () => categoryOptions.find((o) => o.value === form.Category) || null,
    [categoryOptions, form.Category],
  );

  const selectedGate = useMemo(
    () => STUDY_GATE_OPTIONS.find((o) => o.value === form.GateCode) || null,
    [form.GateCode],
  );

  const datePreview = useMemo(
    () =>
      formatMilestoneDatePreview({
        anchorType: form.AnchorType,
        anchorMilestoneCode: form.AnchorMilestoneCode,
        milestoneCode: form.MilestoneCode,
        offsetDays: form.AnchorOffsetDays,
      }),
    [
      form.AnchorType,
      form.AnchorMilestoneCode,
      form.MilestoneCode,
      form.AnchorOffsetDays,
    ],
  );

  const datePreviewHint = useMemo(() => {
    if (form.AnchorOffsetDays === "" || form.AnchorOffsetDays == null) {
      return "Enter offset (days) to see preview.";
    }
    if (form.AnchorType === "AnotherMilestone" && !form.AnchorMilestoneCode) {
      return "Select anchor milestone and offset to see preview.";
    }
    return "Enter formula details to see preview.";
  }, [form.AnchorType, form.AnchorMilestoneCode, form.AnchorOffsetDays]);

  const isReferenceDateAnchor = form.AnchorType === "ReferenceDate";
  const anchorMilestoneEmptyPlaceholder = isReferenceDateAnchor
    ? "Not used"
    : anchorMilestoneOptions.length === 0
      ? "None available"
      : "Select milestone…";
  const tagList = parseTags(form.Tags);
  const codeLocked = readOnly || !!form.MilestoneId;
  const showSetupPanel = !hideDefaultFolder || !hideWorkflowRoles;
  const { sign: offsetSign, digits: offsetDigits } = parseMilestoneOffset(form.AnchorOffsetDays);

  const setOffsetSign = (sign) => {
    setForm((p) => ({
      ...p,
      AnchorOffsetDays: composeMilestoneOffset(sign, parseMilestoneOffset(p.AnchorOffsetDays).digits),
    }));
  };

  const setOffsetDigits = (digits) => {
    setForm((p) => ({
      ...p,
      AnchorOffsetDays: composeMilestoneOffset(
        parseMilestoneOffset(p.AnchorOffsetDays).sign,
        digits,
      ),
    }));
  };

  return (
    <div className="row g-3 milestone-modal-layout">
      <div className="col-lg-5 milestone-modal-left" ref={leftPanelRef}>
        <div className="milestone-section milestone-section--flat">
          <div className="row g-2 milestone-meta-row-compact">
            <div className="col-4">
              <label className="milestone-modal-label milestone-modal-label--compact">
                Code <span className="mandatory">*</span>
              </label>
              <input
                className="form-control form-control-sm milestone-field-compact"
                value={form.MilestoneCode}
                disabled={codeLocked}
                placeholder="SIV"
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    MilestoneCode: e.target.value.toUpperCase(),
                  }))
                }
              />
              {errors.MilestoneCode && (
                <small className="text-danger d-block">{errors.MilestoneCode}</small>
              )}
            </div>
            <div className="col-4">
              <label className="milestone-modal-label milestone-modal-label--compact">Version</label>
              <input
                className="form-control form-control-sm milestone-field-compact"
                value={form.Version || "1.0"}
                disabled
              />
              {readOnly && form.MilestoneId && form.Status === "Published" && (
                <small className="text-muted">Create a draft version to edit.</small>
              )}
            </div>
            <div className="col-4">
              <label className="milestone-modal-label milestone-modal-label--compact">Category</label>
              <div className="milestone-dropdown-compact">
                <Dropdown
                options={categoryOptions}
                isMulti={false}
                selected={selectedCategory}
                disabled={readOnly}
                onSelect={(opt) =>
                  setForm((p) => ({ ...p, Category: opt?.value || "" }))
                }
              />
              </div>
            </div>
            <div className="col-12">
              <label className="milestone-modal-label milestone-modal-label--compact">
                Study gate <span className="mandatory">*</span>
              </label>
              <div className="milestone-dropdown-compact">
                <Dropdown
                  options={STUDY_GATE_OPTIONS}
                  isMulti={false}
                  selected={selectedGate}
                  disabled={readOnly}
                  onSelect={(opt) =>
                    setForm((p) => ({ ...p, GateCode: opt?.value || "" }))
                  }
                />
              </div>
              {errors.GateCode && (
                <small className="text-danger d-block">{errors.GateCode}</small>
              )}
            </div>
            <div className="col-12">
              <label className="milestone-modal-label">
                Name <span className="mandatory">*</span>
              </label>
              <input
                className="form-control form-control-sm"
                disabled={readOnly}
                value={form.MilestoneName}
                placeholder="Site Initiation Visit"
                maxLength={MILESTONE_NAME_MAX_LENGTH}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    MilestoneName: String(e.target.value || "").slice(
                      0,
                      MILESTONE_NAME_MAX_LENGTH,
                    ),
                  }))
                }
              />
              {errors.MilestoneName && (
                <small className="text-danger">{errors.MilestoneName}</small>
              )}
            </div>
            <div className="col-12">
              <label className="milestone-modal-label">Description</label>
              <textarea
                className="form-control form-control-sm milestone-field-box milestone-field-box--description"
                disabled={readOnly}
                value={form.Description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, Description: e.target.value }))
                }
              />
            </div>
            <div className="col-12">
              <label className="milestone-modal-label">Tags</label>
              <div
                className={`milestone-tags-box milestone-field-box milestone-field-box--tags ${readOnly ? "milestone-tags-box--readonly" : ""}`}
                onClick={() => {
                  if (!readOnly) {
                    document.getElementById("milestone-tag-input")?.focus();
                  }
                }}
              >
                {tagList.map((tag) => (
                  <span key={tag} className="milestone-tags-box__chip">
                    {tag}
                    {!readOnly && (
                      <button
                        type="button"
                        className="milestone-tags-box__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveTag(tag);
                        }}
                        aria-label={`Remove ${tag}`}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
                {!readOnly && (
                  <input
                    id="milestone-tag-input"
                    className="milestone-tags-box__input"
                    value={tagInput}
                    placeholder={tagList.length ? "" : "Add tags…"}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" ||
                        e.key === "," ||
                        (e.key === " " && tagInput.trim())
                      ) {
                        e.preventDefault();
                        onAddTag();
                      } else if (
                        e.key === "Backspace" &&
                        !tagInput &&
                        tagList.length
                      ) {
                        onRemoveTag(tagList[tagList.length - 1]);
                      }
                    }}
                    onBlur={() => {
                      if (tagInput.trim()) onAddTag();
                    }}
                  />
                )}
                {readOnly && tagList.length === 0 && (
                  <span className="text-muted small">—</span>
                )}
              </div>
              {!readOnly && (
                <p className="milestone-tags-hint">
                  <i className="fas fa-info-circle" aria-hidden="true" />
                  Press Enter, comma, or space to add a tag.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="milestone-date-formula-box">
          <h6 className="milestone-date-formula-box__title">Expected date formula</h6>
          <div className="milestone-date-formula-grid">
            <div className="milestone-date-formula-grid__anchor">
              <label className="milestone-modal-label milestone-modal-label--compact">Anchor</label>
              <MilestoneSingleOptionPicker
                layout="inline-field"
                value={form.AnchorType || ""}
                options={ANCHOR_TYPES}
                disabled={readOnly}
                showSearch={false}
                addLabel="Select anchor"
                changeLabel="Change anchor"
                emptyPlaceholder="Select anchor…"
                popoverTitle="Anchor"
                popoverSubtitle="Choose anchor type"
                emptyMessage="No anchor types"
                onChange={(nextValue) =>
                  setForm((p) => ({
                    ...p,
                    AnchorType: nextValue || "",
                    AnchorMilestoneCode:
                      nextValue === "ReferenceDate" ? "" : p.AnchorMilestoneCode,
                  }))
                }
              />
            </div>
            <div className="milestone-date-formula-grid__anchor-ms">
              <label className="milestone-modal-label milestone-modal-label--compact">
                Anchor milestone
                {!isReferenceDateAnchor ? <span className="mandatory"> *</span> : null}
              </label>
              <MilestoneSingleOptionPicker
                layout="inline-field"
                value={form.AnchorMilestoneCode || ""}
                options={anchorMilestoneOptions}
                disabled={readOnly || isReferenceDateAnchor}
                invalid={!!errors.AnchorMilestoneCode}
                showSearch
                addLabel="Select milestone"
                changeLabel="Change milestone"
                emptyPlaceholder={anchorMilestoneEmptyPlaceholder}
                disabledPlaceholder="Not used"
                popoverTitle="Anchor milestone"
                popoverSubtitle="Choose a milestone"
                searchPlaceholder="Search milestones..."
                emptyMessage=""
                placeholder="—"
                onChange={(nextValue) =>
                  setForm((p) => ({
                    ...p,
                    AnchorMilestoneCode: nextValue || "",
                  }))
                }
              />
            </div>
            <div className="milestone-date-formula-grid__offset">
              <label className="milestone-modal-label milestone-modal-label--compact">Offset (days)</label>
              <div className="milestone-offset-control milestone-offset-control--formula">
                <input
                  type="text"
                  inputMode="numeric"
                  className="milestone-offset-control__input"
                  disabled={readOnly}
                  placeholder="7"
                  value={offsetDigits}
                  aria-label="Offset days"
                  onChange={(e) => setOffsetDigits(normalizeOffsetDigits(e.target.value))}
                />
                <span className="milestone-offset-control__divider" aria-hidden="true" />
                <div
                  className="milestone-offset-control__toggle"
                  role="group"
                  aria-label="Offset direction"
                >
                  <button
                    type="button"
                    className={`milestone-offset-control__toggle-btn milestone-offset-control__toggle-btn--plus${offsetSign === "+" ? " is-active" : ""}`}
                    disabled={readOnly}
                    aria-pressed={offsetSign === "+"}
                    onClick={() => setOffsetSign("+")}
                  >
                    + days
                  </button>
                  <button
                    type="button"
                    className={`milestone-offset-control__toggle-btn milestone-offset-control__toggle-btn--minus${offsetSign === "-" ? " is-active" : ""}`}
                    disabled={readOnly}
                    aria-pressed={offsetSign === "-"}
                    onClick={() => setOffsetSign("-")}
                  >
                    − days
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="milestone-date-formula-preview-row">
            <label className="milestone-modal-label milestone-modal-label--compact">Preview</label>
            <div
              className={`milestone-date-formula-preview${datePreview ? " has-value" : " is-hint"}`}
              title={datePreview || datePreviewHint}
              aria-live="polite"
            >
              {datePreview || datePreviewHint}
            </div>
          </div>
        </div>
      </div>

      <div
        className="col-lg-7 milestone-modal-right"
        style={leftPanelHeight ? { maxHeight: `${leftPanelHeight}px` } : undefined}
      >
        {showSetupPanel && (
          <div className="row g-3 milestone-setup-row">
            {!hideDefaultFolder && (
              <div className="col-lg-5">
                <div className="milestone-setup-box">
                  <h6 className="milestone-panel-title">Default folder</h6>
                  <MilestoneFolderSelect
                    required
                    folderOptions={folderOptions}
                    folderCategory={normalizeFolderCategory(form.DefaultFolderCategory)}
                    lockedCategory={lockedCategory}
                    selectedFolderName={form.DefaultFolderName}
                    readOnly={readOnly}
                    error={errors.DefaultFolderName}
                    onCategoryChange={(category) =>
                      setForm((p) => {
                        const nextCategory = normalizeFolderCategory(category);
                        const keepFolder = folderOptions.some(
                          (opt) =>
                            opt.value === p.DefaultFolderName &&
                            normalizeFolderCategory(opt.folderCategory) === nextCategory,
                        );
                        const keepAnchor =
                          p.AnchorMilestoneCode &&
                          milestoneLookup.some(
                            (m) =>
                              m.MilestoneCode === p.AnchorMilestoneCode &&
                              milestoneMatchesFolderCategory(m, nextCategory, folderOptions),
                          );
                        return {
                          ...p,
                          DefaultFolderCategory: nextCategory,
                          DefaultFolderName: keepFolder ? p.DefaultFolderName : "",
                          AnchorMilestoneCode: keepAnchor ? p.AnchorMilestoneCode : "",
                        };
                      })
                    }
                    onFolderChange={(folderName) =>
                      setForm((p) => ({ ...p, DefaultFolderName: folderName }))
                    }
                  />
                </div>
              </div>
            )}
            {!hideWorkflowRoles && (
              <div className={`${hideDefaultFolder ? "col-12" : "col-lg-7"}`}>
                <div className="milestone-setup-box">
                  <h6 className="milestone-panel-title">Signature &amp; workflow</h6>
                  <MilestoneWorkflowRoles
                    showTitle={false}
                    form={form}
                    setForm={setForm}
                    errors={errors}
                    readOnly={readOnly}
                    notificationRoles={notificationRoles}
                    onSetUploaderRoles={onSetUploaderRoles}
                    onSetSignerRoles={onSetSignerRoles}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {!hideRecordsGrid && (
          <MilestoneRecordsGrid
            records={form.records || []}
            readOnly={readOnly}
            onChange={(records) => setForm((p) => ({ ...p, records }))}
          />
        )}

        <div className="milestone-notif-panel">
          <h6 className="milestone-panel-title">Notification rules</h6>
          {errors.notifications ? (
            <small className="text-danger d-block mb-2">{errors.notifications}</small>
          ) : null}
          <div className="table-responsive milestone-notif-panel__scroll">
            <table className="table table-sm align-middle mb-0 milestone-notif-panel__table">
              <thead>
                <tr>
                  <th>Trigger</th>
                  <th>Default recipients (roles)</th>
                  <th>Channel</th>
                  <th className="text-center">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {(form.notifications || []).map((rule, index) => (
                  <tr key={rule.TriggerCode || index}>
                    <td className="milestone-notif-panel__trigger">
                      <strong>{rule.TriggerCode}</strong>
                      {rule.TriggerLabel ? (
                        <span className="text-muted ms-1">({rule.TriggerLabel})</span>
                      ) : null}
                    </td>
                    <td className="milestone-notif-panel__recipients">
                      <MilestoneRecipientRoles
                        selectedRoleIds={resolveRecipientRoleIds(
                          rule.DefaultRecipients,
                          notificationRoles,
                        )}
                        allRoles={notificationRoles}
                        disabled={readOnly}
                        showSearch
                        excludeSelectedFromPicker
                        onChange={(roleIds) => onSetRecipients(index, roleIds)}
                      />
                    </td>
                    <td className="milestone-notif-panel__channel">
                      <span className="badge rounded-pill milestone-notif-panel__channel-badge">
                        {NOTIFICATION_CHANNEL}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="form-check form-switch d-inline-flex mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          disabled={readOnly}
                          checked={rule.IsEnabled !== false}
                          onChange={(e) =>
                            onUpdateNotification(index, "IsEnabled", e.target.checked)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {metaFooter ? (
          <div className="milestone-modal-meta-footer">{metaFooter}</div>
        ) : null}
      </div>
    </div>
  );
};

export default MilestoneLibraryForm;
